import React, { useState, useEffect, useRef } from 'react';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import ReactMarkdown from 'react-markdown';

const playBeep = () => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(800, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);

        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start();
        osc.stop(ctx.currentTime + 0.1);
    } catch (e) {
        // Audio playback blocked or unavailable
    }
};

const QuartermasterAI = ({
    playerData,
    tokenBalances,
    userNfts,
    totalMP = 0,
    totalBoost = 0,
    speedBoost = 0,
    currentStamina = 0
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasUnreadAlert, setHasUnreadAlert] = useState(false);
    const [query, setQuery] = useState("");
    const [chatLog, setChatLog] = useState([
        { sender: 'AI', text: "Quartermaster uplink established. State your query, Fighter." }
    ]);
    const [loading, setLoading] = useState(false);
    const chatEndRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
            setHasUnreadAlert(false);
        }
    }, [chatLog, isOpen]);

    const quickPrompts = [
        "How does the Speed scaling formula work?",
        "What is the Zero Stamina penalty?",
        "How do I meet the Investor Protocol?"
    ];

    const askAI = async (overrideQuery = null) => {
        const userMsg = overrideQuery || query;
        if (!userMsg.trim()) return;

        setQuery("");
        setChatLog(prev => [...prev, { sender: 'USER', text: userMsg }]);
        setLoading(true);

        try {
            const fn = httpsCallable(functions, 'askQuartermaster');

            const validHistory = chatLog.map(msg => ({
                role: msg.sender === 'USER' ? 'user' : 'model',
                parts: [{ text: msg.text }]
            }));
            let formattedHistory = validHistory.slice(-10);
            while (formattedHistory.length > 0 && formattedHistory[0].role !== 'user') {
                formattedHistory.shift();
            }

            const firemanBalString = tokenBalances?.['FIREMAN'] || "0";
            const firemanBalNum = parseFloat(firemanBalString.split(' ')[0]) || 0;

            const res = await fn({
                prompt: userMsg,
                history: formattedHistory,
                telemetry: {
                    level: playerData?.level || 1,
                    firemanBalance: firemanBalNum,
                    vaultScore: playerData?.vaultScore || 0,
                    dailyPoints: playerData?.dailyPoints || 0,
                    stamina: currentStamina || playerData?.stamina || 0,
                    maxStamina: playerData?.maxStamina || 100,
                    totalMP: totalMP,
                    totalBoost: totalBoost,
                    speedBoost: speedBoost,
                    investorProtocolActive: (playerData?.vaultScore >= 250000 && playerData?.dailyPoints >= 2500 && (playerData?.level || 1) >= 2) ? "Yes" : "No",
                    inGang: !!playerData?.gangId,
                    totalNftsOwned: userNfts ? userNfts.length : 0
                }
            });

            playBeep();
            setChatLog(prev => [...prev, { sender: 'AI', text: res.data.text }]);
        } catch (e) {
            console.error("UPLINK CRASHED:", e);
            setChatLog(prev => [...prev, { sender: 'AI', text: "ERROR: Communications array damaged. Signal lost." }]);
        }
        setLoading(false);
    };

    return (
        <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999 }}>
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="modern-button"
                    style={{
                        borderRadius: '50%',
                        width: '65px',
                        height: '65px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '30px',
                        boxShadow: hasUnreadAlert ? '0 0 25px rgba(255, 0, 0, 0.8)' : '0 0 15px rgba(0, 229, 255, 0.5)',
                        animation: hasUnreadAlert ? 'pulse-red 1.5s infinite' : 'none',
                        border: hasUnreadAlert ? '2px solid red' : '2px solid #00e5ff',
                        transition: 'all 0.3s ease'
                    }}
                >
                    🤖
                </button>
            )}

            {isOpen && (
                <div style={{
                    backgroundColor: 'rgba(0,0,0,0.95)',
                    border: '1px solid #00e5ff',
                    borderRadius: '8px',
                    padding: '15px',
                    width: '380px',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0px 0px 20px rgba(0, 229, 255, 0.3)'
                }}>
                    <div style={{ borderBottom: '1px dashed #00e5ff', paddingBottom: '10px', marginBottom: '15px', color: '#00e5ff', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ display: 'inline-block', width: '8px', height: '8px', backgroundColor: '#00e5ff', borderRadius: '50%', animation: 'pulse-blue 2s infinite' }}></span>
                            {"// QUARTERMASTER UPLINK //"}
                        </span>
                        <button onClick={() => setIsOpen(false)} style={{ background: 'transparent', border: 'none', color: '#ff4b2b', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}>✕</button>
                    </div>

                    <div style={{
                        height: '380px',
                        overflowY: 'auto',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid #333',
                        borderRadius: '4px',
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                        fontFamily: 'monospace',
                        fontSize: '13px'
                    }}>
                        {chatLog.map((msg, index) => (
                            <div key={index} style={{
                                textAlign: msg.sender === 'USER' ? 'right' : 'left',
                                color: msg.sender === 'USER' ? '#fff' : '#00e5ff',
                                borderLeft: msg.sender === 'AI' ? '3px solid #00e5ff' : 'none',
                                borderRight: msg.sender === 'USER' ? '3px solid #ff9800' : 'none',
                                padding: '5px 10px',
                                backgroundColor: msg.sender === 'USER' ? 'rgba(255, 152, 0, 0.05)' : 'rgba(0, 229, 255, 0.05)',
                                borderRadius: '4px'
                            }}>
                                <span style={{ opacity: 0.6, fontSize: '10px', display: 'block', marginBottom: '4px', marginTop: '2px', color: msg.sender === 'USER' ? '#ff9800' : '#00e5ff' }}>
                                    {msg.sender === 'USER' ? 'FIGHTER' : 'QUARTERMASTER'}
                                </span>
                                <div style={{ lineHeight: '1.4' }}>
                                    {msg.sender === 'AI' ? (
                                        <ReactMarkdown
                                            components={{
                                                p: ({ node, ...props }) => <p style={{ margin: '0 0 8px 0' }} {...props} />,
                                                ul: ({ node, ...props }) => <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px' }} {...props} />,
                                                li: ({ node, ...props }) => <li style={{ marginBottom: '4px' }} {...props} />,
                                                strong: ({ node, ...props }) => <strong style={{ color: '#ffcc00' }} {...props} />,
                                                table: ({ node, ...props }) => <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '8px' }} {...props} />,
                                                th: ({ node, ...props }) => <th style={{ borderBottom: '1px solid #00e5ff', padding: '4px', textAlign: 'left', color: '#ffcc00' }} {...props} />,
                                                td: ({ node, ...props }) => <td style={{ padding: '4px', borderBottom: '1px solid #333' }} {...props} />,
                                            }}
                                        >
                                            {msg.text}
                                        </ReactMarkdown>
                                    ) : (
                                        <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {loading && (
                            <div style={{ color: '#ff9800', paddingLeft: '10px', borderLeft: '3px solid #ff9800', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <em>PROCESSING</em>
                                <span style={{ animation: 'blink 1.4s infinite both' }}>.</span>
                                <span style={{ animation: 'blink 1.4s infinite both', animationDelay: '0.2s' }}>.</span>
                                <span style={{ animation: 'blink 1.4s infinite both', animationDelay: '0.4s' }}>.</span>
                            </div>
                        )}

                        {!loading && chatLog[chatLog.length - 1].sender === 'AI' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '10px', borderTop: '1px solid #333', paddingTop: '10px' }}>
                                <span style={{ fontSize: '10px', color: '#888' }}>SUGGESTED QUERIES:</span>
                                {quickPrompts.map((promptText, i) => (
                                    <button
                                        key={i}
                                        onClick={() => askAI(promptText)}
                                        style={{
                                            background: 'rgba(0, 229, 255, 0.1)',
                                            border: '1px solid rgba(0, 229, 255, 0.3)',
                                            color: '#00e5ff',
                                            padding: '6px 10px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '11px',
                                            textAlign: 'left',
                                            fontFamily: 'monospace',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseOver={(e) => e.target.style.background = 'rgba(0, 229, 255, 0.2)'}
                                        onMouseOut={(e) => e.target.style.background = 'rgba(0, 229, 255, 0.1)'}
                                    >
                                        &gt; {promptText}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div ref={chatEndRef} />
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '15px' }}>
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && askAI()}
                            placeholder="State your query, Fighter..."
                            style={{
                                flex: 1,
                                background: '#111',
                                color: '#fff',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                padding: '10px',
                                fontFamily: 'monospace',
                                outline: 'none',
                                fontSize: '12px'
                            }}
                            disabled={loading}
                        />
                        <button
                            onClick={() => askAI()}
                            disabled={loading || !query.trim()}
                            className="modern-button"
                            style={{ margin: 0, padding: '10px 15px', borderRadius: '4px', fontSize: '12px', opacity: (loading || !query.trim()) ? 0.5 : 1 }}
                        >
                            SEND
                        </button>
                    </div>
                </div>
            )}

            <style>{`
        @keyframes pulse-red {
          0% { box-shadow: 0 0 15px rgba(255, 0, 0, 0.5); transform: scale(1); }
          50% { box-shadow: 0 0 25px rgba(255, 0, 0, 1); transform: scale(1.05); }
          100% { box-shadow: 0 0 15px rgba(255, 0, 0, 0.5); transform: scale(1); }
        }
        @keyframes pulse-blue {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes blink {
          0% { opacity: 0.2; }
          20% { opacity: 1; }
          100% { opacity: 0.2; }
        }
      `}</style>
        </div>
    );
};

export default QuartermasterAI;